<script lang="ts">
import { defineComponent, PropType, ref, computed, watch, onMounted, toRef } from 'vue'
import { formPartial } from '@/models/formPartial'
import type { Ref } from 'vue'

/**
 * @fileoverview 単一のチェックボックスアイテム（多数の中から任意の数選ぶ類のものではなく、「規約に同意する」のような、単一の選択アイテムのためのコンポーネント）
 */
export default defineComponent({
  name: 'CheckboxItem',

  props: {
    /**
     * バリデーションの名前空間
     * @see 詳細は下記コンポーネントの namespace を参照
     *      @/components/validation/ValidationProvider.vue
     */
    namespace: {
      type: String,
      default: '',
    },


    /**
     * 初期選択状態が bind される value
     */
    modelValue: {
      type: [String, Number, Boolean],
      default: undefined,
    },

    /**
     * フォームの name 属性
     */
    name: {
      type: String,
      default: '',
    },

    /**
     * チェックボックスに対応する文言・文章
     */
    label: {
      type: String,
      default: '',
    },

    /**
     * チェックボックスの value 属性に bind される値
     * @description 未指定の場合、コンポーネント側でデフォルト値として「true」がバインドされる
     */
    value: {
      type: [String, Number, Boolean],
      default: undefined,
    },

    /**
     * チェックボックス未チェックの場合に送られる（hidden に bind される）値
     * @description HTML の仕様上、チェックボックスが未チェックの場合、POST アクション時に何も値は送られないが、未チェック時にもなにがしかの値を明示的に送りたい場合に使用する。
     *              未指定の場合は未チェック時は何も送られない（HTML 仕様準拠）が、この props に明示的に値を渡すことで、未チェック時にも渡された値が post される（false 用 hidden が生成される）
     */
    falseValue: {
      type: [String, Number, Boolean],
      default: undefined,
    },

    /**
     * disabled 属性にわたす非活性フラグ
     */
    isDisabled: {
      type: Boolean,
      default: false,
    },

    validationScheme: {
      type: Object as PropType<ValidationScheme>,
      default: () => ({}),
    },
  },

  emits: ['update:modelValue', 'error'],

  setup(props, { emit }) {
    const { hasError, isRequired, createErrorMessage } = formPartial(props.name, toRef(props, 'validationScheme'))

    const localValue: Ref<string | number | boolean | undefined> = ref()

    // チェックボックスの value 属性にバインドする値管理用
    const localBindValue = ref()

    /**
     * 未チェック時用の hidden を生成するかどうか
     * @description 未チェック時用の値が明示的に指定されていて、且つ localValue に値が入っていない＝チェックされていない場合に未チェック時用の hidden を生成
     */
    const hasFalseValue = computed(() => props.falseValue !== undefined && localValue.value !== props.value)

    /**
     * props が正しく設定されているかどうか
     * @description 未チェック時用の値が明示的に渡されており、且つチェック時用の値と未チェック時用の値が同一の場合は不正な状態として検知
     */
    const isValidProps = computed(() => props.falseValue === undefined || props.value !== props.falseValue)

    /**
     * チェックボックスがチェックされてるかどうか
     */
    const isChecked = computed(() => localValue.value !== undefined && localValue.value === props.value)

    watch(() => props.modelValue, (newValue) => {
      if (localValue.value !== newValue) localValue.value = newValue
    })

    // props で渡される value に変更があった際の検知
    // 変更後の値が undefined であった場合は、デフォルト値として「true」をバインドする。
    watch(() => props.value, (newValue) => {
      if (localBindValue.value !== newValue) {
        localBindValue.value = newValue !== undefined
          ? newValue
          : true
      }
    })

    onMounted(() => {
      if (!isValidProps.value) {
        console.error('Invalid Props Value: The values for checking and unchecking are the same.')
      }
      localValue.value = props.modelValue
      localBindValue.value = props.value !== undefined
        ? props.value
        : true

      // false value が定義されており、且つバインドされた model value が 'TRUE' の値でも 'FALSE' の値でもない場合は、'FALSE' の値で初期化
      if (props.falseValue !== undefined && ![localBindValue.value, props.falseValue].includes(props.modelValue)) {
        localValue.value = props.falseValue
        emit('update:modelValue', localValue.value)
      }
    })

    return {
      // data
      localValue,
      localBindValue,

      // computed
      hasError,
      isRequired,
      hasFalseValue,
      isValidProps,
      isChecked,

      // methods
      createErrorMessage,
    }
  }
})
</script>

<template>
  <ValidationProvider
    :value="modelValue"
    :false-value="falseValue"
    :namespace="namespace"
    :field="name"
    :scheme="validationScheme"
    :messages="createErrorMessage(true, false, label)"
    @error="$emit('error', $event)">
    <label
      v-if="isValidProps"
      class="label"
      :data-is-disabled="isDisabled"
      :data-is-error="hasError">
      <template v-if="falseValue !== undefined">
        <input
          v-if="hasFalseValue"
          class="input"
          type="hidden"
          :name="name ? name : undefined"
          :value="falseValue">
        <input
          v-model="localValue"
          class="input"
          type="checkbox"
          :name="name ? name : undefined"
          :value="localBindValue"
          :true-value="localBindValue"
          :false-value="falseValue"
          :disabled="isDisabled"
          :data-is-error="hasError"
          :checked="isChecked"
          @change="$emit('update:modelValue', localValue)">
      </template>
      <template v-else>
        <input
          v-model="localValue"
          class="input"
          type="checkbox"
          :name="name ? name : undefined"
          :value="localBindValue"
          :disabled="isDisabled"
          :data-is-error="hasError"
          :checked="isChecked"
          @change="$emit('update:modelValue', localValue)">
      </template>
      <span
        class="span"
        v-html="label" />
      <span
        v-if="isRequired"
        class="span u-text__required">
        必須
      </span>
    </label>
    <strong
      v-else
      class="strong">
      項目に設定されている値が不正です。
    </strong>
  </ValidationProvider>
</template>

<style lang="sass" scoped>
.label
  // normalize のチェックボックスを基準としているので、具体的な変数の値は normalize（≒ mixins.sass の checkboxStyle）を参照
  $icon-size-px: 22
  $icon-size-em: math.div($icon-size-px, $font-normal-px)
  $margin-size: 0.5em
  display: inline-block
  padding-left: ($icon-size-em + $margin-size)

  .input[type=checkbox]
    margin-left: -#{$icon-size-em + $margin-size}
</style>
